import { API_ENDPOINT } from "@/constants/appConstants";
import { JOB_DETAILS_QUERY_KEY, SIMILAR_JOBS_QUERY_KEY } from "@/lib/queryKeys";
import { UseQueryResult, useQuery } from "@tanstack/react-query";

export interface Job {
  applicationData: Object;
  jobDetails: Object;
}

export interface JobMetaData {
  paging: Object;
  resultId: string;
}

export interface SimilarJobsResponse {
  data: Object[];
  meta: JobMetaData[];
  filters: any[];
}

export interface JobStatusResponse {
  applicationHistory: { uuid: string; text: string; statusUpdatedAt: number }[];
}

export const jobStatusAPI = async <T>(
  jodId?: string,
  kiwiJobId?: string,
  userUuid?: string,
  isAutoApplied: boolean = false,
): Promise<T | Error> => {
  try {
    const host = window.location.host;
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.getJobDetails}?${jodId ? `jobId=${jodId}&` : kiwiJobId ? `kiwiJobId=${kiwiJobId}&` : ""}${userUuid ? `userUuid=${userUuid}&` : ""}${isAutoApplied ? `isAutoApplied=${isAutoApplied}&` : ""}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    const data: T = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching data, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const similarJobsAPI = async (
  jobId?: string,
  loggedInUser: boolean = true,
  experience: number = 0,
): Promise<SimilarJobsResponse> => {
  try {
    const host = window.location.host;
    const response = await fetch(
      `${API_ENDPOINT(host).middleware.getSimilarJobs}?jobId=${jobId}&loggedInUser=${loggedInUser}&experience=${experience}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    const data: SimilarJobsResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching data, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const jobSaveUnsaveAPI = async <T>(
  jobId?: number,
  type?: string,
): Promise<T | Error> => {
  try {
    const host = window.location.host;

    const response = await fetch(
      `${API_ENDPOINT(host).middleware.jobSaveUnsaveAPI}?type=${type}`,
      {
        method: type == "save" ? "POST" : "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobId: jobId,
        }),
      },
    );

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while saving this job, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const prepAIQuestionsAPI = async <T>(
  jobId?: number,
): Promise<T | Error> => {
  try {
    const host = window.location.host;

    const response = await fetch(
      `${API_ENDPOINT(host).middleware.getPrepAIQuestions}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          jobId: jobId,
        }),
      },
    );

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(
        `Error occurred while fetching prepAI questions, ${JSON.stringify(error)}`,
      );
    }
  }
};

export const useJobDetails = (
  jodId: string,
  kiwiJobId?: string,
  userUuid?: string,
  isAutoApplied: boolean = false,
  enableFetch: boolean = true,
): UseQueryResult<JobStatusResponse> => {
  return useQuery({
    queryKey: [
      JOB_DETAILS_QUERY_KEY,
      jodId,
      kiwiJobId,
      userUuid,
      isAutoApplied,
    ],
    queryFn: () =>
      jobStatusAPI<JobStatusResponse>(
        jodId,
        kiwiJobId,
        userUuid,
        isAutoApplied,
      ),
    enabled: enableFetch,
  });
};

export const useSimilarJobs = (
  jobId: string,
  loggedInUser: boolean,
  experience: number,
): UseQueryResult<SimilarJobsResponse> => {
  return useQuery({
    queryKey: [SIMILAR_JOBS_QUERY_KEY, jobId, loggedInUser, experience],
    queryFn: () => similarJobsAPI(jobId, loggedInUser, experience),
    enabled: !!jobId,
  });
};
